/* step 12: Size guide (bottom sheet on phones, dialog on desktop). Created by the step 01 gate, Oct 17, 2026. Owner: step 12 only. Guard calls into other steps (if (window.X) ...).
 *
 * C3: PBSPhone.sizeGuide = window.PBSSizeGuide = { open(opts) -> boolean, close(), isOpen() }.
 * opts = { garment: 'tee' | 'sweat', size, trigger, stack, desktop, onPick(size) }.
 * Markup is snippets/pbs-size-guide.liquid (#pbs-size-guide, 12a). open() returns false
 * (caller keeps its fallback) on desktop without opts.desktop, with no markup, or for an
 * unknown garment. The sheet never reads a product and never writes to the page: the
 * caller passes the garment and the current size, and gets the pick back through onPick.
 * C25: sessionStorage pbs-sg-unit ('in' or 'cm'), every read and write in try/catch.
 *
 * GATE DEPENDENCY: the gate's overlay.open() ignores opts.desktop and refuses at 750px
 * and up unless the root carries data-pbs-desktop. So open() sets that attribute on the
 * root itself when opts.desktop is true on desktop, and removes it otherwise. pbs-phone.js
 * is not edited.
 */
(function () {
  'use strict';

  var P = window.PBSPhone;
  if (!P || !P.overlay) return;

  var SCALE = ['S', 'M', 'L', 'XL', '2XL'];
  var UNIT_KEY = 'pbs-sg-unit';
  var WHY = {
    up: 'Sized up one for that slouchy, borrowed-from-the-break-room look.',
    down: 'Sized down one, since the cut is relaxed.',
    same: 'True to size in our relaxed unisex cut.'
  };

  var root = null;        /* the sheet element, bound once per element */
  var isOpen = false;
  var garment = null;
  var picked = null;
  var onPick = null;
  var fa = null;          /* "your usual size" answer, kept for the page view */
  var fb = null;          /* fit answer: Fitted, True to size, Oversized */

  function findRoot() {
    return document.querySelector('[data-pbs-overlay="size-guide"]');
  }

  function all(sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  /* ---- units ---- */
  function readUnit() {
    try { return window.sessionStorage.getItem(UNIT_KEY) === 'cm' ? 'cm' : 'in'; } catch (e) { return 'in'; }
  }

  function setUnit(unit, save) {
    unit = unit === 'cm' ? 'cm' : 'in';
    root.classList.toggle('is-cm', unit === 'cm');
    all('[data-pbs-sgs-unit]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-pbs-sgs-unit') === unit));
    });
    if (save) {
      try { window.sessionStorage.setItem(UNIT_KEY, unit); } catch (e) { /* storage blocked: unit just will not persist */ }
    }
  }

  /* ---- selection ---- */
  function rowFor(size) {
    if (!size) return null;
    var rows = all('table[data-pbs-sgs-garment="' + garment + '"] tr[data-pbs-sgs-row]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-pbs-sgs-row') === size) return rows[i];
    }
    return null;
  }

  function select(size) {
    var row = rowFor(size);
    picked = row ? size : null;
    all('tr[data-pbs-sgs-row]').forEach(function (tr) {
      var on = tr === row;
      tr.classList.toggle('is-on', on);
      var pick = tr.querySelector('.pbs-sgs__pick');
      if (pick) pick.setAttribute('aria-pressed', String(on));
    });
    var btn = root.querySelector('[data-pbs-sgs-choose]');
    if (btn) {
      btn.disabled = !picked;
      btn.textContent = picked ? 'Choose size ' + picked : 'Tap a size to choose it';
    }
  }

  /* ---- suggestion ---- */
  function recommend() {
    if (!fa || !fb) return null;
    var shift = fb === 'Oversized' ? 1 : (fb === 'Fitted' ? -1 : 0);
    var i = SCALE.indexOf(fa) + shift;
    i = Math.max(0, Math.min(SCALE.length - 1, i));
    return { size: SCALE[i], why: shift > 0 ? WHY.up : (shift < 0 ? WHY.down : WHY.same) };
  }

  function showSuggestion() {
    var out = root.querySelector('[data-pbs-sgs-out]');
    var rec = recommend();
    if (!out) return rec;
    if (rec) {
      out.innerHTML = 'We would pick <b>' + rec.size + '</b> for you. ' + rec.why;
      out.hidden = false;
    } else {
      out.hidden = true;
    }
    return rec;
  }

  function pressGroup(attr, value) {
    all('[' + attr + ']').forEach(function (b) {
      b.setAttribute('aria-pressed', String(!!value && b.getAttribute(attr) === value));
    });
  }

  /* ---- keyboard ----
     The capture-phase listener sits on window, ahead of every document listener.
     Quick add's own onKey is on document (pbs-quick-add.js section 2): without
     this, Escape would close Quick add underneath as well, and Tab would pull
     focus back into Quick add because focus is "outside" its sheet. Stopping the
     event at window in the capture phase means Quick add needs no change. */
  function focusable(el) {
    var list = el.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(list, function (n) {
      return !n.disabled
        && n.getAttribute('aria-hidden') !== 'true'
        && (n.offsetWidth > 0 || n.offsetHeight > 0);
    });
  }

  function onKey(e) {
    if (!isOpen || !root) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    e.stopPropagation();
    var f = focusable(root);
    if (!f.length) { e.preventDefault(); return; }
    var first = f[0];
    var last = f[f.length - 1];
    var here = document.activeElement;
    var inside = root.contains(here);
    if (e.shiftKey && (here === first || !inside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (here === last || !inside)) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---- delegated clicks, bound once per root element ---- */
  function onClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var el = t.closest('[data-pbs-sgs-unit]');
    if (el) { setUnit(el.getAttribute('data-pbs-sgs-unit'), true); return; }

    el = t.closest('[data-pbs-sgs-fa]');
    if (el) {
      fa = el.getAttribute('data-pbs-sgs-fa');
      pressGroup('data-pbs-sgs-fa', fa);
      var rec = showSuggestion();
      if (rec) select(rec.size);
      return;
    }
    el = t.closest('[data-pbs-sgs-fb]');
    if (el) {
      fb = el.getAttribute('data-pbs-sgs-fb');
      pressGroup('data-pbs-sgs-fb', fb);
      var rec2 = showSuggestion();
      if (rec2) select(rec2.size);
      return;
    }
    el = t.closest('[data-pbs-sgs-choose]');
    if (el) {
      if (!picked || el.disabled) return;
      var cb = onPick;
      var s = picked;
      close();
      if (cb) cb(s);
      return;
    }
    el = t.closest('tr[data-pbs-sgs-row]');
    if (el) select(el.getAttribute('data-pbs-sgs-row'));
  }

  function bind() {
    if (root.__pbsSgsBound) return;
    root.__pbsSgsBound = true;
    root.addEventListener('click', onClick);
  }

  /* ---- lifecycle ---- */
  function cleanup() {
    isOpen = false;
    onPick = null;
    window.removeEventListener('keydown', onKey, true);
  }

  function open(opts) {
    opts = opts || {};
    var g = opts.garment;
    if (g !== 'tee' && g !== 'sweat') return false;
    var desktopOk = opts.desktop === true;
    if (!P.isPhone() && !desktopOk) return false;
    var el = findRoot();
    if (!el) return false;

    if (isOpen) cleanup();   /* a second open() while open starts clean */
    root = el;
    bind();
    garment = g;

    /* The gate's overlay.open() ignores opts.desktop; desktop is data-pbs-desktop on the root. */
    if (!P.isPhone() && desktopOk) root.setAttribute('data-pbs-desktop', '');
    else root.removeAttribute('data-pbs-desktop');

    all('[data-pbs-sgs-garment]').forEach(function (n) {
      n.hidden = n.getAttribute('data-pbs-sgs-garment') !== g;
    });
    setUnit(readUnit(), false);
    pressGroup('data-pbs-sgs-fa', fa);
    pressGroup('data-pbs-sgs-fb', fb);
    showSuggestion();
    var want = typeof opts.size === 'string' ? opts.size.replace(/^\s+|\s+$/g, '').toUpperCase() : null;
    select(want);
    var body = root.querySelector('[data-pbs-sgs-body]');
    if (body) body.scrollTop = 0;

    onPick = typeof opts.onPick === 'function' ? opts.onPick : null;
    isOpen = true;
    window.addEventListener('keydown', onKey, { capture: true });

    var ok = P.overlay.open('size-guide', {
      trigger: opts.trigger,
      stack: !!opts.stack,
      desktop: !!opts.desktop
    });
    if (!ok) { cleanup(); return false; }
    return true;
  }

  function close() {
    if (!isOpen) return;
    cleanup();
    P.overlay.close();
  }

  /* Backdrop tap, the X, swipe down and the window growing past 749px all close through
     the overlay core; clean up on its event too. cleanup() is idempotent. */
  document.addEventListener('pbs:overlay:close', function (e) {
    if (e && e.detail && e.detail.name === 'size-guide') cleanup();
  });

  P.sizeGuide = {
    open: open,
    close: close,
    isOpen: function () { return isOpen; }
  };
  window.PBSSizeGuide = P.sizeGuide;
}());
